import { FormProvider } from 'react-hook-form';
import { Box, Button, LinearProgress, Stack, Typography } from '@mui/material';
import React from 'react';

import { useDocuments } from 'hooks/useDocuments';
import { useServerTranslation } from 'hooks/useServerTranslation';
import FormInput from '../Forms/FormInput';
import FormCheckbox from '../Forms/FormCheckbox';
import DateInput from '../Forms/DateInput';
import { InlineTextLink } from '../common/InlineTextLink';
import { SelfRegisterCustomerFormProps } from './SelfRegisterCustomerFormProps';
import { useSelfRegisterCustomerFormHook } from './useSelfRegisterCustomerFormHook';

export function SelfRegisterCustomerForm({ consultantId, boxProps }: SelfRegisterCustomerFormProps) {
    // @todo Remove and refactor hardcoded document links
    const { t } = useServerTranslation();

    const { integrityPolicyConsultantCustomerHref, termsAndConditionsHref } = useDocuments();

    const {
        methods,
        loading,
        formComplete,
        handleSubmit,
    } = useSelfRegisterCustomerFormHook(consultantId);

    if (formComplete) {
        return (
            <Box {...boxProps}>
                <Typography
                    sx={{
                        marginBottom: '30px',
                        fontWeight: 'bold',
                        fontSize: '20px',
                    }}
                >
                    {t('profile.register-account-form.success-message')}
                </Typography>
            </Box>
        );
    }

    return (
        <Box {...boxProps}>
            <Typography
                variant="h2"
                sx={{
                    marginBottom: '30px',
                    fontWeight: 'bold',
                    fontSize: '20px',
                }}
            >
                {t('profile.create-account')}
            </Typography>
            <Typography
                sx={{
                    marginBottom: '30px',
                    fontSize: '16px',
                }}
            >
                {t('profile.register-account-form.form-description')}
            </Typography>
            <FormProvider {...methods}>
                <form onSubmit={handleSubmit}>
                    <Stack columnGap={2} sx={{ flexWrap: 'wrap' }} direction="row">
                        <Box sx={{ flexBasis: '45%' }}>
                            <FormInput
                                name="first_name"
                                label={t('form-fields.user.first-name')}
                                variant="outlined"
                            />
                        </Box>
                        <Box sx={{ flexBasis: '45%' }}>
                            <FormInput
                                name="last_name"
                                label={t('form-fields.user.last-name')}
                                variant="outlined"
                            />
                        </Box>
                        <Box sx={{ flexBasis: '45%' }}>
                            <FormInput
                                name="email"
                                label={t('form-fields.user.email')}
                                variant="outlined"
                            />
                        </Box>
                        <Box sx={{ flexBasis: '45%' }}>
                            <FormInput
                                name="mobile"
                                label={t('form-fields.user.mobile')}
                                variant="outlined"
                            />
                        </Box>
                        <Box sx={{ flexBasis: '45%' }}>
                            <DateInput
                                name="date_of_birth"
                                datePickerSlotProps={{
                                    textField: {
                                        size: 'small',
                                        variant: 'outlined',
                                        fullWidth: true,
                                    },
                                }}
                                label={t('form-fields.user.date-of-birth')}
                                showSeparateLabel
                            />
                        </Box>
                    </Stack>

                    <Stack gap={2} sx={{ flexWrap: 'wrap' }} direction="row">
                        <Box sx={{ flexBasis: '45%' }}>
                            <FormInput
                                name="co"
                                label={t('form-fields.user.co')}
                                variant="outlined"
                            />
                        </Box>
                        <Box sx={{ flexBasis: '45%' }}>
                            <FormInput
                                name="address"
                                label={t('form-fields.user.address')}
                                variant="outlined"
                            />
                        </Box>
                        <Box sx={{ flexBasis: '45%' }}>
                            <FormInput
                                name="postal_code"
                                label={t('form-fields.user.postal-code')}
                                variant="outlined"
                            />
                        </Box>
                        <Box sx={{ flexBasis: '45%' }}>
                            <FormInput
                                name="city"
                                label={t('form-fields.user.city')}
                                variant="outlined"
                            />
                        </Box>
                    </Stack>

                    <Stack gap={2} sx={{ flexWrap: 'wrap' }} direction="row">
                        <Box sx={{ flexBasis: '45%' }}>
                            <FormInput
                                label={t('form-fields.user.password')}
                                name="password"
                                type="password"
                                variant="outlined"
                            />
                        </Box>
                        <Box sx={{ flexBasis: '45%' }}>
                            <FormInput
                                label={t('form-fields.user.password-again')}
                                name="password_again"
                                type="password"
                                variant="outlined"
                            />
                        </Box>
                    </Stack>
                    <Stack direction="column" sx={{ marginBottom: '20px' }}>
                        <FormCheckbox
                            name="career_interest"
                            label={t('profile.register-account-form.career-interest-label')}
                        />
                        <FormCheckbox
                            name="class_interest"
                            label={t('profile.register-account-form.class-interest-label')}
                        />
                        <FormCheckbox
                            name="newsletter"
                            label={t('profile.register-account-form.newsletter-consent-label')}
                        />
                        <FormCheckbox
                            name="communication_consultant"
                            label={t('profile.register-account-form.communication-consultant-label')}
                        />
                        <FormCheckbox
                            name="user_agreement"
                            label={(
                                <>
                                    {t('profile.register-account-form.form-consent-label')}
                                    <InlineTextLink
                                        href={termsAndConditionsHref}
                                    >
                                        {t('profile.register-account-form.user-agreement-link-text')}
                                    </InlineTextLink>
                                </>
                            )}
                        />
                    </Stack>
                    <Typography sx={{ marginBottom: '24px' }}>
                        {t('profile.register-account-form.form-consent')}
                        <InlineTextLink
                            href={integrityPolicyConsultantCustomerHref}
                        >
                            {t('profile.register-account-form.integrity-policy-link-text')}
                        </InlineTextLink>
                    </Typography>
                    {loading ? <LinearProgress /> : null}
                    <Button
                        variant="contained"
                        type="submit"
                        disabled={loading}
                    >
                        {t('profile.create-account')}
                    </Button>
                </form>
            </FormProvider>
        </Box>
    );
}
